/**
 * वर्तमान सर्वे (N-PAC, 3 Nov 2026): one form per person — door to door (a volunteer at the booth, the phone's
 * GPS attached so the war room sees where the work was done) or telecaller (the team in the war room, no GPS).
 * Booth, mohalla / village (from the booth's places), name, mobile, age, profession, organisation role, the
 * candidate's standing, the main problems (add more), a suggestion. Submit → the answer joins the surveyor's
 * record; "दूसरा सर्वे" clears the person and keeps booth and mohalla.
 *
 * An answer is saved on the phone first and sent at once; without network it waits and is retried every
 * 15 seconds, when the network returns and when the screen opens — never lost.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@core/api";
import { findBoothByCode, normalizeDigits } from "@core/boothCode";
import { apiErrorText } from "@core/messages";
import type { BoothPlace, SurveyMine } from "../field/api";
import { addAnswer, blankForm, buildAnswer, checkForm, dropSent, MAX_PROBLEMS, queueKey, readQueue, SEND_BATCH, SURVEY_STATUSES, type FormErrors, type SurveyAnswer, type SurveyForm, type SurveyMethod, type SurveyStatus } from "../field/survey";
import { formatTime } from "../i18n";
import { makeId, prefs, watchGps } from "../platform/browser";
import type { Fix } from "@core/location";
import { useApp, useQuery, useSession } from "../ui/context";
import { Banner, Button, Card, Field } from "../ui/components";

const RETRY_MS = 15_000;
const GPS_WAIT_MS = 15_000;
type Msg = { kind: "ok" | "warn" | "error" | "info"; text: string } | null;
const OTHER = "__other__";

export function SurveyScreen() {
  const { app, t } = useApp();
  const { assemblyId } = useSession();
  const duties = (app.user?.duties ?? null) as string[] | null;
  const admin = app.user?.role !== "surveyor";
  // the booth duty "survey" (standing estimate) also opens the door-to-door answers; no duties = every booth duty
  const canD2d = admin || !duties || !duties.length || duties.includes("d2d") || duties.includes("survey");
  const canTele = admin || !!duties?.includes("tele");
  const [method, setMethod] = useState<SurveyMethod>(() => (prefs.get("survey.method") === "tele" && canTele ? "tele" : canD2d ? "d2d" : "tele"));
  const pickMethod = (m: SurveyMethod) => {
    setMethod(m);
    prefs.set("survey.method", m);
  };

  // the login's booths
  const all = useQuery(async () => (assemblyId ? app.core.store.listBooths(assemblyId) : []), [assemblyId], ["booths"]) ?? [];
  const mine = useMemo(() => all.filter((b) => app.canEdit(b)), [all, app]);
  const [code, setCode] = useState("");
  useEffect(() => {
    void app.lastBooth().then((c) => {
      if (c && mine.some((b) => b.code === c)) setCode((cur) => cur || c);
      else if (mine.length && mine.length <= 12) setCode((cur) => cur || mine[0].code);
    });
  }, [app, mine]);
  const booth = useMemo(() => (code.trim() ? findBoothByCode(mine, code) : null), [mine, code]);
  const boothProblem = !code.trim() || booth ? null : findBoothByCode(all, code) ? t.boothNotAssigned(normalizeDigits(code.trim()).toUpperCase()) : t.boothUnknown(normalizeDigits(code.trim()).toUpperCase());

  return (
    <div data-testid="survey">
      <h2 style={{ margin: "0 0 4px" }}>📋 {t.csTitle}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {t.csExplain}
      </p>
      {canD2d && canTele ? (
        <div className="field">
          <div className="label">{t.csMethod}</div>
          <div className="chips sv-method">
            <button type="button" className={`chip${method === "d2d" ? " chip-on" : ""}`} onClick={() => pickMethod("d2d")} data-testid="survey-d2d">
              🚶 {t.csD2d}
            </button>
            <button type="button" className={`chip${method === "tele" ? " chip-on" : ""}`} onClick={() => pickMethod("tele")} data-testid="survey-tele">
              📞 {t.csTele}
            </button>
          </div>
        </div>
      ) : (
        <div className="field">
          <span className="bs-chip k-dalit" data-testid="survey-method-fixed">
            {canD2d ? `🚶 ${t.csD2d}` : `📞 ${t.csTele}`}
          </span>
        </div>
      )}
      {!mine.length ? <Banner kind="warn">{t.csNoBooth}</Banner> : null}
      {mine.length > 0 && mine.length <= 12 ? (
        <div className="field">
          <div className="label">{t.csBooth}</div>
          <div className="chips">
            {mine.map((b) => (
              <button
                key={b.code}
                type="button"
                className={`chip${booth?.code === b.code ? " chip-on" : ""}`}
                onClick={() => {
                  setCode(b.code);
                  void app.rememberBooth(b.code);
                }}
                data-testid={`survey-booth-${b.code}`}
              >
                {t.boothNo} {b.code}
              </button>
            ))}
          </div>
          {booth ? <div className="hint">{`${booth.name}${booth.panchayat ? ` • ${booth.panchayat}` : ""}`}</div> : null}
        </div>
      ) : mine.length > 12 ? (
        <Field label={t.csBoothNo} name="survey-booth" value={code} onChange={setCode} placeholder={t.boothNoHint} error={boothProblem} hint={booth ? `${booth.name} • ${booth.panchayat ?? ""}` : undefined} />
      ) : null}
      {booth && assemblyId ? <SurveyForm key={`${assemblyId}:${booth.code}:${method}`} aid={assemblyId} code={booth.code} method={method} /> : null}
      {assemblyId ? <MyRecord aid={assemblyId} /> : null}
      <p className="muted" style={{ fontSize: 14 }}>
        {t.csPrivacy}
      </p>
    </div>
  );
}

/* ====================================================================== the form */

// the booth's village / mohalla places, kept for the session so a second answer needs no fetch
const placeCache = new Map<string, BoothPlace[]>();

function SurveyForm({ aid, code, method }: { aid: string; code: string; method: SurveyMethod }) {
  const { app, t, lang } = useApp();
  const [places, setPlaces] = useState<BoothPlace[] | null>(placeCache.get(`${aid}:${code}`) ?? null);
  const [placesOffline, setPlacesOffline] = useState(false);
  const [placePick, setPlacePick] = useState<string>(() => prefs.get(`survey.place:${aid}:${code}`) ?? "");
  const [placeText, setPlaceText] = useState("");
  const [form, setForm] = useState<SurveyForm>(blankForm);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [done, setDone] = useState<string | null>(null);
  const [queue, setQueueState] = useState<SurveyAnswer[]>(() => readQueue(prefs.get(queueKey(aid))));
  const queueRef = useRef(queue);
  const setQueue = (q: SurveyAnswer[]) => {
    queueRef.current = q;
    prefs.set(queueKey(aid), q.length ? JSON.stringify(q) : null);
    setQueueState(q);
  };
  const sending = useRef(false);

  useEffect(() => {
    if (places) return;
    let off = false;
    app.online((api) => api.boothPlaces(aid, code)).then(
      (r) => {
        if (off) return;
        const v = r.items.filter((p) => p.kind === "village");
        placeCache.set(`${aid}:${code}`, v);
        setPlaces(v);
        setPlacesOffline(false);
      },
      () => !off && setPlacesOffline(true)
    );
    return () => {
      off = true;
    };
  }, [app, aid, code, places]);
  const pickPlace = (v: string) => {
    setPlacePick(v);
    prefs.set(`survey.place:${aid}:${code}`, v || null);
  };
  const placeId = placePick && placePick !== OTHER ? Number(placePick) : null;
  const placeName = placeId != null ? (places?.find((p) => p.id === placeId)?.name ?? null) : placePick === OTHER || !places?.length ? placeText : null;

  // GPS for door to door: watched while the form is open, so the fix is ready when the answer is submitted
  const [fix, setFix] = useState<Fix | null>(null);
  const [gps, setGps] = useState<"finding" | "ok" | "none" | "denied">("finding");
  const [gpsTry, setGpsTry] = useState(0);
  useEffect(() => {
    if (method !== "d2d") return;
    setGps("finding");
    const w = watchGps(
      (f) => {
        setFix((prev) => (!prev || (f.accuracyM ?? 9e9) <= (prev.accuracyM ?? 9e9) || Date.now() - prev.timestamp > 60_000 ? f : prev));
        setGps("ok");
      },
      (e) => setGps(e === "denied" ? "denied" : "none")
    );
    const timer = window.setTimeout(() => setGps((g) => (g === "finding" ? "none" : g)), GPS_WAIT_MS);
    return () => {
      w.stop();
      window.clearTimeout(timer);
    };
  }, [method, gpsTry]);

  const set = <K extends keyof SurveyForm>(k: K) => (v: SurveyForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const errs: FormErrors = checkForm(form);
  const errText = (k: keyof FormErrors) => (!touched || !errs[k] ? null : errs[k] === "required" ? t.csRequired : k === "mobile" ? t.csMobileBad : k === "age" ? t.csAgeBad : t.csRequired);

  /** send what is queued; the server's answer, or null (offline / refused) */
  const flush = useCallback(async (): Promise<{ saved: { clientId: string; duplicate: boolean; flags: string[] }[] } | null> => {
    if (sending.current || !queueRef.current.length) return null;
    sending.current = true;
    const batch = queueRef.current.slice(0, SEND_BATCH);
    let sent = false;
    try {
      const r = await app.online((api) => api.surveySend(aid, batch));
      setQueue(dropSent(queueRef.current, batch.map((b) => b.clientId)));
      sent = true;
      window.dispatchEvent(new CustomEvent("survey-sent"));
      return r;
    } catch (e) {
      if (e instanceof ApiError) {
        // refused for good (not the login's duty / booth, bad data): keep the others, drop this batch's bad one
        if (e.status === 403 || e.status === 400) {
          setMsg({ kind: "error", text: apiErrorText(e, lang) ?? e.message });
          if (batch.length === 1) setQueue(dropSent(queueRef.current, [batch[0].clientId]));
        }
      }
      return null;
    } finally {
      sending.current = false;
      if (sent && queueRef.current.length) setTimeout(() => void flush(), 50);
    }
  }, [app, aid, lang]);
  useEffect(() => {
    void flush();
    const again = () => void flush();
    const id = window.setInterval(again, RETRY_MS);
    window.addEventListener("online", again);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("online", again);
    };
  }, [flush]);

  const submit = async () => {
    setTouched(true);
    setMsg(null);
    if (Object.keys(errs).length) return;
    const a = buildAnswer(form, { clientId: makeId(), boothCode: code, method, placeId, placeName, fix: fix ? { lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM } : null, now: Date.now() });
    setQueue(addAnswer(queueRef.current, a));
    setBusy(true);
    for (let i = 0; sending.current && i < 100; i++) await new Promise((ok) => setTimeout(ok, 100));
    const r = await flush();
    setBusy(false);
    if (queueRef.current.some((q) => q.clientId === a.clientId) && !r) {
      setDone(a.name);
      setMsg({ kind: "info", text: t.csSavedQueued(a.name) });
      return;
    }
    setDone(a.name);
    setMsg({ kind: "ok", text: t.csSaved(a.name) });
  };
  const another = () => {
    setForm(blankForm());
    setTouched(false);
    setDone(null);
    setMsg(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Card testId="survey-form">
      <div className="field">
        <div className="label">{t.csPlace}</div>
        {places === null && !placesOffline ? (
          <div className="muted">…</div>
        ) : places && places.length ? (
          <>
            <select className="input" value={placePick} onChange={(e) => pickPlace(e.target.value)} data-testid="survey-place">
              <option value="">{t.csPlacePick}</option>
              {places.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
              <option value={OTHER}>{t.csPlaceOther}</option>
            </select>
            {placePick === OTHER ? <input className="input" style={{ marginTop: 8 }} value={placeText} onChange={(e) => setPlaceText(e.target.value)} placeholder={t.csPlaceName} maxLength={160} data-testid="survey-place-text" /> : null}
          </>
        ) : (
          <>
            <input className="input" value={placeText} onChange={(e) => setPlaceText(e.target.value)} placeholder={t.csPlaceName} maxLength={160} data-testid="survey-place-text" />
            <div className="hint">{placesOffline ? t.csPlaceOffline : t.csPlaceNone}</div>
          </>
        )}
      </div>

      {done ? (
        <>
          {msg ? <Banner kind={msg.kind} testId="survey-msg">{msg.text}</Banner> : null}
          <Button big onClick={another} testId="survey-another">
            ➕ {t.csAnother}
          </Button>
          {queue.length ? (
            <p className="muted" style={{ marginBottom: 0 }} data-testid="survey-pending">
              ⏳ {t.csPending(queue.length)}
            </p>
          ) : null}
        </>
      ) : (
        <>
          <Field label={t.csName} name="sv-name" value={form.name} onChange={set("name")} error={errText("name")} maxLength={120} autoComplete="off" />
          <Field label={t.csMobile} name="sv-mobile" value={form.mobile} onChange={set("mobile")} inputMode="tel" error={errText("mobile")} maxLength={16} autoComplete="off" />
          <Field label={t.csAge} name="sv-age" value={form.age} onChange={set("age")} inputMode="numeric" error={errText("age")} maxLength={3} />
          <Field label={t.csProfession} name="sv-prof" value={form.profession} onChange={set("profession")} maxLength={80} />
          <Field label={t.csOrgRole} name="sv-org" value={form.orgRole} onChange={set("orgRole")} maxLength={160} />
          <div className={`field${touched && errs.status ? " sv-err" : ""}`}>
            <label className="label" htmlFor="sv-status">
              {t.csStatus}
            </label>
            <select id="sv-status" className={`input${touched && errs.status ? " input-error" : ""}`} value={form.status} onChange={(e) => set("status")(e.target.value as SurveyStatus | "")} data-testid="survey-status">
              <option value="">{t.csStatusPick}</option>
              {SURVEY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === "strong" ? t.csStrong : s === "neutral" ? t.csNeutral : t.csWeak}
                </option>
              ))}
            </select>
            {touched && errs.status ? <div className="hint" style={{ color: "var(--danger)" }}>{t.csRequired}</div> : null}
          </div>
          <div className="field">
            <div className="label">{t.csProblems}</div>
            <div className="stack">
              {form.problems.map((p, i) => (
                <div key={i} className="sv-prob">
                  <input
                    className="input"
                    value={p}
                    maxLength={200}
                    placeholder={t.csProblemPh(i + 1)}
                    onChange={(e) => set("problems")(form.problems.map((x, j) => (j === i ? e.target.value : x)))}
                    data-testid={`survey-problem-${i}`}
                  />
                  {form.problems.length > 1 ? (
                    <button type="button" className="chip" onClick={() => set("problems")(form.problems.filter((_, j) => j !== i))} aria-label={t.csRemove}>
                      ✕
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            {form.problems.length < MAX_PROBLEMS ? (
              <Button variant="secondary" onClick={() => set("problems")([...form.problems, ""])} testId="survey-add-problem">
                {t.csAddProblem}
              </Button>
            ) : null}
          </div>
          <Field label={t.csSuggestion} name="sv-sugg" value={form.suggestion} onChange={set("suggestion")} multiline maxLength={500} />
          {method === "d2d" ? (
            <div className="field">
              <div className="label">{t.csLocation}</div>
              <div className="sv-gps">
                <span data-testid="survey-gps" style={gps === "ok" ? { color: "var(--ok)", fontWeight: 700 } : gps === "finding" ? undefined : { color: "var(--danger)", fontWeight: 700 }}>
                  {gps === "finding" ? t.csGpsFinding : gps === "ok" && fix ? t.csGpsOk(fix.accuracyM != null ? String(Math.round(fix.accuracyM)) : "?") : gps === "denied" ? t.csGpsDenied : t.csGpsNone}
                </span>
                <Button variant="secondary" onClick={() => setGpsTry((n) => n + 1)} disabled={gps === "finding"}>
                  {t.csGpsAgain}
                </Button>
              </div>
            </div>
          ) : null}
          {msg ? <Banner kind={msg.kind} testId="survey-msg">{msg.text}</Banner> : null}
          <Button big busy={busy} onClick={() => void submit()} testId="survey-submit">
            {t.csSubmit}
          </Button>
          {queue.length ? (
            <p className="muted" style={{ marginBottom: 0 }} data-testid="survey-pending">
              ⏳ {t.csPending(queue.length)}
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

/* ====================================================================== my record */

function MyRecord({ aid }: { aid: string }) {
  const { app, t, lang } = useApp();
  const [mine, setMine] = useState<SurveyMine | null>(null);
  const [offline, setOffline] = useState(false);
  const load = useCallback(() => {
    app.online((api) => api.surveyMine(aid)).then(
      (m) => {
        setMine(m);
        setOffline(false);
      },
      () => setOffline(true)
    );
  }, [app, aid]);
  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener("survey-sent", on);
    window.addEventListener("online", on);
    return () => {
      window.removeEventListener("survey-sent", on);
      window.removeEventListener("online", on);
    };
  }, [load]);
  const statusText = (s: string) => (s === "strong" ? t.csStrong : s === "neutral" ? t.csNeutral : t.csWeak);
  return (
    <Card testId="survey-mine">
      <h3 style={{ marginTop: 0 }}>🧾 {t.csMine}</h3>
      {mine ? (
        <>
          <div className="stats">
            <div className="stat">
              <b data-testid="survey-today">{mine.today}</b>
              <span>{t.csToday}</span>
            </div>
            <div className="stat">
              <b data-testid="survey-total">{mine.total}</b>
              <span>{t.csTotal}</span>
            </div>
            <div className="stat">
              <b>{mine.booths}</b>
              <span>{t.csBooths}</span>
            </div>
          </div>
          <div className="label" style={{ marginTop: 12 }}>
            {t.csRecent}
          </div>
          {mine.recent.length ? (
            <ul className="sv-recent">
              {mine.recent.slice(0, 10).map((r) => (
                <li key={r.id}>
                  <div>
                    <b>{r.name}</b> <span className="muted">· {t.boothNo} {r.booth}{r.place ? ` · ${r.place}` : ""}</span>
                  </div>
                  <div className="sv-recent-meta">
                    <span className={`bs-chip sv-${r.status}`}>{statusText(r.status)}</span>
                    <span className="muted">{r.method === "d2d" ? "🚶" : "📞"} {formatTime(r.at, lang)}</span>
                    {r.flags.map((f) => (
                      <span key={f} className="bs-chip sv-flag">
                        {t.csFlag(f)}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t.csNoneYet}</p>
          )}
        </>
      ) : (
        <p className="muted">{offline ? t.csMineOffline : "…"}</p>
      )}
    </Card>
  );
}
